import { isNil } from 'lodash';
import { RefObject, useEffect, useRef } from 'react';

// Floor for very short containers, so the trigger distance is never ~0px
const MIN_END_REACHED_DISTANCE = 200;

interface UseEndReachedOptions {
  scrollRef: RefObject<HTMLDivElement | null>;
  onEndReached?: () => void;
  onEndReachedThreshold: number;
  /** Re-arms the check when a new page lands */
  itemCount: number;
}

export function useEndReached({
  scrollRef,
  onEndReached,
  onEndReachedThreshold,
  itemCount,
}: UseEndReachedOptions) {
  // Keep the callback out of the effect deps so re-renders don't re-attach listeners
  const onEndReachedRef = useRef(onEndReached);
  const hasFiredEndReached = useRef(false);
  const hasEndReachedHandler = !isNil(onEndReached);

  useEffect(() => {
    // Keep onEndReached callback up to date
    onEndReachedRef.current = onEndReached;
  }, [onEndReached]);

  useEffect(() => {
    const el = scrollRef.current;
    if (isNil(el) || !hasEndReachedHandler) {
      return;
    }
    // New items arrived (or this is the first run): allow firing again
    hasFiredEndReached.current = false;

    const checkEndReached = () => {
      const distanceToEnd = el.scrollHeight - el.scrollTop - el.clientHeight;
      const triggerDistance = Math.max(
        el.clientHeight * onEndReachedThreshold,
        MIN_END_REACHED_DISTANCE,
      );
      if (distanceToEnd > triggerDistance) {
        hasFiredEndReached.current = false;
        return;
      }
      if (!hasFiredEndReached.current) {
        hasFiredEndReached.current = true;
        onEndReachedRef.current?.();
      }
    };

    // Content shorter than the viewport never scrolls, so check up front too
    checkEndReached();
    el.addEventListener('scroll', checkEndReached, { passive: true });
    const observer = new ResizeObserver(checkEndReached);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkEndReached);
      observer.disconnect();
    };
  }, [hasEndReachedHandler, onEndReachedThreshold, itemCount, scrollRef]);
}
